/**
 * 风场流体图层 — 基于 leaflet-velocity
 * 将 WindDecoder 解码后的 U/V 数据转换为 L.velocityLayer 所需格式
 *
 * 格点映射:
 *   行 0 = 70°N, 行 280 = 0° (纬度步长 0.25°)
 *   列 0 = 0°E,  列 280 = 360° (经度步长 1.2857°)
 */

import L from 'leaflet'
window.L = L
import 'leaflet-velocity/dist/leaflet-velocity.js'

const GRID_ROWS = 281
const GRID_COLS = 281

/**
 * 将解码后的风场数据转换为 leaflet-velocity 格式
 * @param {Object} windDecoder - WindDecoder 实例 (uData/vData 已填充)
 * @returns {Array} velocityLayer data 数组
 */
function buildVelocityData(windDecoder) {
  const uArr = []
  const vArr = []

  // Y轴反转: 后端编码时 img_array[GRID_ROWS - row, col] 做了Y翻转,
  // 使画布 top=南 bottom=北, 解码后需反转回来使 row 0 = 70°N
  for (let row = GRID_ROWS - 1; row >= 0; row--) {
    for (let col = 0; col < GRID_COLS; col++) {
      const idx = row * GRID_COLS + col
      uArr.push(windDecoder.uData[idx])
      vArr.push(windDecoder.vData[idx])
    }
  }

  const header = {
    parameterCategory: 2,
    parameterNumber: 2,     // U 分量
    lo1: 70,                // 经度范围 70°E ~ 140°E
    la1: 70,                // 纬度范围 70°N ~ 0°
    lo2: 140,
    la2: 0,
    dx: 70.0 / (GRID_COLS - 1),   // 0.25° 经度步长
    dy: 70.0 / (GRID_ROWS - 1),   // 0.25° 纬度步长
    nx: GRID_COLS,
    ny: GRID_ROWS,
    gridDefinitionTemplate: 0
  }

  return [
    { header: { ...header, parameterNumber: 2 }, data: uArr },
    { header: { ...header, parameterNumber: 3 }, data: vArr }
  ]
}

/**
 * 创建 velocityLayer 并叠加到地图
 * @param {Object} windDecoder
 * @param {Object} map - Leaflet map 实例
 * @param {Object} [options] - velocityLayer 额外选项
 * @returns {L.VelocityLayer}
 */
function createWindVelocityLayer(windDecoder, map, options = {}) {
  const data = buildVelocityData(windDecoder)

  const layer = L.velocityLayer({
    displayValues: true,
    displayOptions: {
      velocityType: '风速',
      position: 'bottomleft',
      emptyString: '无风速数据',
      angleConvention: 'bearingCW',
      showCardinal: true,
      speedUnit: 'ms',
      directionString: '风向',
      speedString: '风速'
    },
    data: data,
    minVelocity: 0,
    maxVelocity: 25,
    velocityScale: 0.02,
    particleMultiplier: 1 / 120,
    lineWidth: 2,
    particleAge: 120,
    opacity: 0.95,
    colorScale: [
      'rgb(0, 255, 128)',
      'rgb(0, 230, 64)',
      'rgb(64, 255, 0)',
      'rgb(192, 255, 0)',
      'rgb(255, 230, 0)',
      'rgb(255, 192, 0)',
      'rgb(255, 128, 0)',
      'rgb(255, 64, 0)',
      'rgb(255, 0, 64)',
      'rgb(230, 0, 128)',
      'rgb(200, 0, 200)'
    ],
    ...options
  })

  layer.addTo(map)
  return layer
}

export { buildVelocityData, createWindVelocityLayer }
